import React, { useEffect, useRef, useState } from 'react';

// Add CSS for progress animation
const style = document.createElement('style');
style.textContent = `
  @keyframes progress {
    from { width: 0%; }
    to { width: 100%; }
  }
`;
document.head.appendChild(style);

const WS_URL = process.env.NODE_ENV === 'production' 
  ? 'wss://chatflow-backend-14st.onrender.com' 
  : 'ws://localhost:3001';

export default function App() {
  const [connected, setConnected] = useState(false);
  const [clientId, setClientId] = useState(null);
  const [conversations, setConversations] = useState({});
  const [onlineUsers, setOnlineUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [input, setInput] = useState('');
  const [userName, setUserName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [userFirstName, setUserFirstName] = useState('');
  const [userFullName, setUserFullName] = useState('');
  const [showNameForm, setShowNameForm] = useState(true);
  const [authError, setAuthError] = useState('');
  const [isRegistering, setIsRegistering] = useState(false);
  const [registrationSuccess, setRegistrationSuccess] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [emailVerificationSent, setEmailVerificationSent] = useState(false);
  const [verificationCode, setVerificationCode] = useState('');
  const [showVerification, setShowVerification] = useState(false);

  const [isAdmin, setIsAdmin] = useState(false);
  const [adminStats, setAdminStats] = useState({});
  const [showPasswordReset, setShowPasswordReset] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetToken, setResetToken] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmNewPassword, setConfirmNewPassword] = useState('');
  const [showNewPasswordForm, setShowNewPasswordForm] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const wsRef = useRef(null);

  // Check for saved login on app startup
  useEffect(() => {
    const savedUser = localStorage.getItem('chatflow_user');
    if (savedUser) {
      try {
        const userData = JSON.parse(savedUser);
        setUserName(userData.email);
        setUserFullName(userData.fullName);
        setUserFirstName(userData.firstName);
        setShowNameForm(false);
      } catch (error) {
        localStorage.removeItem('chatflow_user');
      }
    }
  }, []);

  useEffect(() => {
    const ws = new WebSocket(WS_URL);
    wsRef.current = ws;

    ws.onopen = () => {
      console.log('WebSocket connected');
      setConnected(true);
    };

    ws.onmessage = (event) => {
      const msg = JSON.parse(event.data);

      if(msg.type === 'welcome') {
        setClientId(msg.clientId);
      }
      if(msg.type === 'authSuccess') {
        setShowNameForm(false);
        setAuthError('');
        if(msg.user) {
          setUserFullName(msg.user.fullName);
          setUserFirstName(msg.user.fullName.split(' ')[0]);
          setUserName(msg.user.email);
          // Save user data to localStorage
          localStorage.setItem('chatflow_user', JSON.stringify({
            email: msg.user.email,
            fullName: msg.user.fullName,
            firstName: msg.user.fullName.split(' ')[0]
          }));
        }
      }
      if(msg.type === 'adminAuthSuccess') {
        setShowNameForm(false);
        setAuthError('');
        setIsAdmin(true);
      }
      if(msg.type === 'adminStats') {
        setAdminStats(msg.stats);
      }
      if(msg.type === 'authError') {
        setAuthError(msg.message);
      }
      if(msg.type === 'registerSuccess') {
        setAuthError('');
        setShowVerification(true);
        setEmailVerificationSent(true);
      }
      if(msg.type === 'verificationSuccess') {
        setAuthError('');
        setShowVerification(false);
        setRegistrationSuccess(true);
        // Show success message and return to login after 3 seconds
        setTimeout(() => {
          setRegistrationSuccess(false);
          setIsRegistering(false);
          setShowNameForm(true);
          setUserName('');
          setPassword('');
          setConfirmPassword('');
          setEmail('');
          setFirstName('');
          setLastName('');
          setVerificationCode('');
        }, 3000);
      }
      if(msg.type === 'verificationError') {
        setAuthError(msg.message);
      }
      if(msg.type === 'resetEmailSent') {
        setAuthError('');
        setResetSuccess(true);
      }
      if(msg.type === 'resetEmailError') {
        setAuthError(msg.message);
      }
      if(msg.type === 'passwordResetSuccess') {
        setAuthError('');
        setShowNewPasswordForm(false);
        setShowPasswordReset(false);
        setResetSuccess(false);
        setResetEmail('');
        setResetToken('');
        setNewPassword('');
        setConfirmNewPassword('');
        alert('Password reset successful! Please login with your new password.');
      }
      if(msg.type === 'passwordResetError') {
        setAuthError(msg.message);
      }
      if(msg.type === 'registerError') {
        setAuthError(msg.message);
      }
      if(msg.type === 'userList') {
        setOnlineUsers(msg.users.filter(u => u.id !== msg.currentClientId));
      }
      if(msg.type === 'privateMessage') {
        const isMyMessage = msg.from === clientId;
        const conversationKey = isMyMessage ? msg.toName : msg.fromName;
        
        console.log('Received message:', {
          from: msg.from,
          to: msg.to,
          fromName: msg.fromName,
          toName: msg.toName,
          text: msg.text,
          isMyMessage,
          conversationKey
        });
        
        setConversations(prev => ({
          ...prev,
          [conversationKey]: [...(prev[conversationKey] || []), {
            from: msg.from,
            fromName: msg.fromName,
            text: msg.text,
            timestamp: msg.timestamp,
            isMyMessage
          }]
        }));
      }
      if(msg.type === 'conversationHistory') {
        console.log('Received conversation history:', msg);
        setConversations(prev => ({
          ...prev,
          [msg.withUser]: msg.messages.map(m => ({
            from: m.fromUsername === userName ? clientId : 'other',
            fromName: m.fromUsername,
            text: m.text,
            timestamp: new Date(m.timestamp).getTime(),
            currentClientId: clientId
          }))
        }));
      }
      if(msg.type === 'messageError') {
        console.error('Message error:', msg.message);
        setAuthError('Message failed: ' + msg.message);
        // Clear error after 3 seconds
        setTimeout(() => setAuthError(''), 3000);
      }
    };

    ws.onclose = () => {
      console.log('WebSocket disconnected');
      setConnected(false);
    };
    ws.onerror = (err) => {
      console.error('WebSocket error:', err);
      setConnected(false);
    };

    return () => ws.close();
  }, []);

  function sendMessage() {
    if(wsRef.current && input.trim() !== '' && selectedUser) {
      const messageText = input.trim();
      console.log('Sending message to:', selectedUser);
      
      // Optimistically add message to conversation
      setConversations(prev => ({
        ...prev,
        [selectedUser.name]: [...(prev[selectedUser.name] || []), {
          from: clientId,
          fromName: userFullName || userName,
          text: messageText,
          timestamp: Date.now(),
          isMyMessage: true
        }]
      }));
      
      wsRef.current.send(JSON.stringify({ 
        type: 'privateMessage', 
        text: messageText, 
        to: selectedUser.id
      }));
      setInput('');
    }
  }
  
  function selectUser(user) {
    console.log('Selecting user:', user);
    setSelectedUser(user);
    // Request conversation history when selecting a user
    if(wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      console.log('Requesting conversation history with:', user.name);
      wsRef.current.send(JSON.stringify({
        type: 'getConversationHistory',
        withUser: user.name
      }));
    }
  }

  function handleVerification() {
    console.log('Verification clicked, code:', verificationCode);
    
    if (verificationCode.length !== 6) {
      setAuthError('Please enter a valid 6-digit code');
      return;
    }
    
    setAuthError(''); // Clear previous errors
    
    if(wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      console.log('Sending verification request');
      wsRef.current.send(JSON.stringify({
        type: 'verifyEmail',
        email: email.trim(),
        code: verificationCode.trim()
      }));
    } else {
      setAuthError('Connection lost. Please refresh and try again.');
    }
  }
  
  function resendVerificationCode() {
    if(wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'resendVerification',
        email: email.trim(),
        fullName: `${firstName.trim()} ${lastName.trim()}`
      }));
    }
    setAuthError('');
  }

  function handlePasswordReset() {
    if (!resetEmail.trim() || !resetEmail.includes('@')) {
      setAuthError('Please enter a valid email address');
      return;
    }
    
    if(wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'requestPasswordReset',
        email: resetEmail.trim()
      }));
    }
  }

  function handleNewPassword() {
    if (!newPassword.trim() || newPassword.length < 6) {
      setAuthError('Password must be at least 6 characters');
      return;
    }
    if (newPassword !== confirmNewPassword) {
      setAuthError('Passwords do not match');
      return;
    }
    if (!resetToken.trim()) {
      setAuthError('Invalid reset token');
      return;
    }
    
    if(wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify({
        type: 'resetPassword',
        email: resetEmail.trim(),
        token: resetToken.trim(),
        newPassword: newPassword.trim()
      }));
    }
  }

  function handleSubmit() {
    console.log('Submit clicked, isRegistering:', isRegistering);

    console.log('Username:', userName);
    console.log('Password length:', password.length);
    
    if (isRegistering) {
      console.log('Registration data:', { firstName, lastName, email, userName, password, confirmPassword });
      
      // Validation for registration
      if (!firstName.trim()) {
        setAuthError('First name is required');
        return;
      }
      if (!lastName.trim()) {
        setAuthError('Last name is required');
        return;
      }
      if (!email.trim() || !email.includes('@')) {
        setAuthError('Valid email is required');
        return;
      }

      if (!password.trim() || password.length < 6) {
        setAuthError('Password must be at least 6 characters');
        return;
      }
      if (password !== confirmPassword) {
        setAuthError('Passwords do not match');
        return;
      }
      
      setAuthError(''); // Clear errors
      console.log('Sending registration request');
      
      // Send registration data
      if(wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
        wsRef.current.send(JSON.stringify({ 
          type: 'register',
          fullName: `${firstName.trim()} ${lastName.trim()}`,
          email: email.trim(),
          password: password.trim()
        }));
      } else {
        setAuthError('Connection lost. Please refresh and try again.');
      }
    } else {
      // Login validation
      if(userName.trim() !== '' && password.trim() !== '') {
        if(wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
          // Check if admin credentials
          const isAdminLogin = userName.trim() === 'admin' && password.trim() === 'admin123';
          const authType = isAdminLogin ? 'adminAuthenticate' : 'authenticate';
          
          console.log('Sending auth request:', authType);
          console.log('WebSocket state:', wsRef.current.readyState);
          
          if(isAdminLogin) {
            wsRef.current.send(JSON.stringify({ 
              type: authType, 
              userName: userName.trim(),
              password: password.trim()
            }));
          } else {
            wsRef.current.send(JSON.stringify({ 
              type: authType, 
              email: userName.trim(), // Using userName field for email input
              password: password.trim()
            }));
          }
        } else {
          console.error('WebSocket not connected! State:', wsRef.current?.readyState);
          setAuthError('Connection lost. Please refresh the page.');
        }
      }
    }
  }

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      sendMessage();
    }
  };

  function handleLogout() {
    setShowLogoutConfirm(true);
  }

  function confirmLogout() {
    // Clear saved user data
    localStorage.removeItem('chatflow_user');
    
    // Reset all state
    setShowNameForm(true);
    setUserName('');
    setPassword('');
    setUserFullName('');
    setUserFirstName('');
    setConversations({});
    setSelectedUser(null);
    setOnlineUsers([]);
    setShowLogoutConfirm(false);
    setAuthError('');
    
    // Close WebSocket connection
    if (wsRef.current) {
      wsRef.current.close();
    }
  }

  function cancelLogout() {
    setShowLogoutConfirm(false);
  }

  if (registrationSuccess) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{
          background: 'rgba(255,255,255,0.95)',
          borderRadius: '15px',
          padding: '40px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
          backdropFilter: 'blur(10px)',
          textAlign: 'center',
          maxWidth: '400px',
          width: '90%'
        }}>
          <div style={{
            fontSize: '4rem',
            marginBottom: '20px'
          }}>
            ✅
          </div>
          <h2 style={{ margin: '0 0 20px 0', color: '#065f46' }}>Account Created Successfully!</h2>
          <p style={{ margin: '0 0 30px 0', color: '#6b7280' }}>
            Your email has been verified and your account is now active.
            Redirecting to login page...
          </p>
          <div style={{
            width: '100%',
            height: '4px',
            background: '#e5e7eb',
            borderRadius: '2px',
            overflow: 'hidden'
          }}>
            <div style={{
              width: '100%',
              height: '100%',
              background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
              animation: 'progress 3s linear'
            }}></div>
          </div>
        </div>
      </div>
    );
  }

  if (showVerification) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center'
      }}>
        <div style={{
          background: 'rgba(255,255,255,0.95)',
          borderRadius: '15px',
          padding: '40px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
          backdropFilter: 'blur(10px)',
          textAlign: 'center',
          maxWidth: '400px',
          width: '90%'
        }}>
          <h2 style={{ margin: '0 0 20px 0', color: '#374151' }}>📧 Verify Your Email</h2>
          <p style={{ margin: '0 0 30px 0', color: '#6b7280' }}>
            We've sent a 6-digit verification code to<br/>
            <strong>{email}</strong>
          </p>
          
          {authError && (
            <div style={{
              background: '#fee2e2',
              color: '#dc2626',
              padding: '10px 15px',
              borderRadius: '8px',
              marginBottom: '20px',
              fontSize: '0.9rem',
              textAlign: 'center'
            }}>
              {authError}
            </div>
          )}
          
          <input
            value={verificationCode}
            onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
            placeholder="Enter 6-digit code"
            style={{
              width: '100%',
              padding: '12px 16px',
              border: '2px solid #e5e7eb',
              borderRadius: '25px',
              fontSize: '1.2rem',
              outline: 'none',
              marginBottom: '20px',
              boxSizing: 'border-box',
              textAlign: 'center',
              letterSpacing: '2px'
            }}
            maxLength="6"
            onKeyPress={(e) => e.key === 'Enter' && handleVerification()}
          />
          
          <button
            onClick={handleVerification}
            disabled={verificationCode.length !== 6}
            style={{
              padding: '12px 30px',
              background: (verificationCode.length === 6) ? 'linear-gradient(135deg, #3b82f6, #1d4ed8)' : '#d1d5db',
              color: 'white',
              border: 'none',
              borderRadius: '25px',
              fontSize: '1rem',
              fontWeight: '600',
              cursor: (verificationCode.length === 6) ? 'pointer' : 'not-allowed',
              marginBottom: '15px',
              width: '100%'
            }}
          >
            Verify Email
          </button>
          
          <button
            onClick={resendVerificationCode}
            style={{
              padding: '8px 20px',
              background: 'transparent',
              color: '#3b82f6',
              border: '1px solid #3b82f6',
              borderRadius: '20px',
              fontSize: '0.9rem',
              cursor: 'pointer'
            }}
          >
            Resend Code
          </button>
          
          <div style={{
            marginTop: '20px',
            fontSize: '0.8rem',
            color: '#9ca3af',
            textAlign: 'center'
          }}>
            Code expires in 5 minutes<br/>
            <span style={{ color: '#00a884', fontWeight: 'bold' }}>
              Check your email inbox for the verification code
            </span>
          </div>
        </div>
      </div>
    );
  }

  if (showNameForm) {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#f8fafc',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px'
      }}>
        {/* ChatFlow Logo */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          marginBottom: '40px'
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            background: '#3b82f6',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            marginRight: '12px'
          }}>
            💬
          </div>
          <h1 style={{
            margin: '0',
            fontSize: '2rem',
            fontWeight: '600',
            color: '#3b82f6'
          }}>
            ChatFlow
          </h1>
        </div>

        <div style={{
          background: 'white',
          borderRadius: '12px',
          padding: '40px',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05), 0 10px 15px rgba(0, 0, 0, 0.1)',
          width: '100%',
          maxWidth: '400px'
        }}>
          <h2 style={{
            margin: '0 0 30px 0',
            fontSize: '1.5rem',
            fontWeight: '600',
            color: '#1f2937',
            textAlign: 'center'
          }}>
            {isRegistering ? 'Create an account' : 'Sign in to your account'}
          </h2>
          

          

          
          {registrationSuccess && (
            <div style={{
              background: '#d1fae5',
              color: '#065f46',
              padding: '12px 16px',
              borderRadius: '8px',
              marginBottom: '20px',
              fontSize: '0.9rem',
              textAlign: 'center',
              fontWeight: '500'
            }}>
              ✓ Account created successfully! Please login with your credentials.
            </div>
          )}
          
          {!connected && (
            <div style={{
              background: '#fef2f2',
              color: '#dc2626',
              padding: '12px 16px',
              borderRadius: '8px',
              marginBottom: '20px',
              fontSize: '0.9rem',
              textAlign: 'center',
              border: '1px solid #fecaca'
            }}>
              ⚠️ Not connected to server. Please check if server is running.
            </div>
          )}
          
          {authError && (
            <div style={{
              background: '#fef2f2',
              color: '#dc2626',
              padding: '12px 16px',
              borderRadius: '8px',
              marginBottom: '20px',
              fontSize: '0.9rem',
              textAlign: 'center',
              border: '1px solid #fecaca'
            }}>
              {authError}
            </div>
          )}
          
          {isRegistering && (
            <>
              <label style={{
                display: 'block',
                fontSize: '0.9rem',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '6px'
              }}>First Name</label>
              <input
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="Enter your first name"
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '0.9rem',
                  outline: 'none',
                  marginBottom: '16px',
                  boxSizing: 'border-box',
                  background: '#f9fafb'
                }}
              />
              
              <label style={{
                display: 'block',
                fontSize: '0.9rem',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '6px'
              }}>Last Name</label>
              <input
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="Enter your last name"
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '0.9rem',
                  outline: 'none',
                  marginBottom: '16px',
                  boxSizing: 'border-box',
                  background: '#f9fafb'
                }}
              />
            </>
          )}
          
          <label style={{
            display: 'block',
            fontSize: '0.9rem',
            fontWeight: '500',
            color: '#374151',
            marginBottom: '6px'
          }}>{isRegistering ? 'Email Address' : 'Email Address'}</label>
          <input
            type="text"
            value={isRegistering ? email : userName}
            onChange={(e) => isRegistering ? setEmail(e.target.value) : setUserName(e.target.value)}
            placeholder={isRegistering ? "your@email.com" : "Email or admin username"}
            style={{
              width: '100%',
              padding: '12px 16px',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              fontSize: '0.9rem',
              outline: 'none',
              marginBottom: '16px',
              boxSizing: 'border-box',
              background: '#f9fafb'
            }}
          />
          

          
          <label style={{
            display: 'block',
            fontSize: '0.9rem',
            fontWeight: '500',
            color: '#374151',
            marginBottom: '6px'
          }}>Password</label>
          <input
            type="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleSubmit()}
            placeholder={isRegistering ? "Create password (min 6 chars)" : "Enter your password"}
            style={{
              width: '100%',
              padding: '12px 16px',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              fontSize: '0.9rem',
              outline: 'none',
              marginBottom: isRegistering ? '16px' : '6px',
              boxSizing: 'border-box',
              background: '#f9fafb'
            }}
          />
          
          {!isRegistering && (
            <div style={{
              textAlign: 'right',
              marginBottom: '20px'
            }}>
              <button 
                onClick={() => setShowPasswordReset(true)}
                style={{
                background: 'none',
                border: 'none',
                color: '#3b82f6',
                fontSize: '0.85rem',
                cursor: 'pointer',
                textDecoration: 'underline'
              }}>
                Password forgotten?
              </button>
            </div>
          )}
          
          {isRegistering && (
            <>
              <label style={{
                display: 'block',
                fontSize: '0.9rem',
                fontWeight: '500',
                color: '#374151',
                marginBottom: '6px'
              }}>Repeat password</label>
              <input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Type your password again"
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: '1px solid #d1d5db',
                  borderRadius: '8px',
                  fontSize: '0.9rem',
                  outline: 'none',
                  marginBottom: '20px',
                  boxSizing: 'border-box',
                  background: '#f9fafb'
                }}
              />
            </>
          )}
          <button
            onClick={handleSubmit}
            disabled={isRegistering ? (!firstName.trim() || !lastName.trim() || !email.trim() || !password.trim() || !confirmPassword.trim()) : (!userName.trim() || !password.trim())}
            style={{
              width: '100%',
              padding: '12px 16px',
              background: (isRegistering ? (firstName.trim() && lastName.trim() && email.trim() && password.trim() && confirmPassword.trim()) : (userName.trim() && password.trim())) ? '#1f2937' : '#9ca3af',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              fontSize: '0.9rem',
              fontWeight: '600',
              cursor: (isRegistering ? (firstName.trim() && lastName.trim() && email.trim() && password.trim() && confirmPassword.trim()) : (userName.trim() && password.trim())) ? 'pointer' : 'not-allowed',
              marginBottom: '20px'
            }}
          >
            {isRegistering ? 'Create Account' : 'Sign In'}
          </button>
          
          <div style={{
            textAlign: 'center',
            fontSize: '0.9rem',
            color: '#6b7280'
          }}>
            {isRegistering ? (
              <>
                Already have an account?{' '}
                <button
                  onClick={() => {
                    setIsRegistering(false);
                    setAuthError('');
                    setRegistrationSuccess(false);
                    setUserName('');
                    setPassword('');
                    setConfirmPassword('');
                    setEmail('');
                    setFirstName('');
                    setLastName('');
                    setShowVerification(false);
                    setEmailVerificationSent(false);
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#3b82f6',
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    fontSize: '0.9rem'
                  }}
                >
                  Sign In
                </button>
              </>
            ) : (
              <>
                Do not have an account yet?{' '}
                <button
                  onClick={() => {
                    setIsRegistering(true);
                    setAuthError('');
                    setRegistrationSuccess(false);
                    setUserName('');
                    setPassword('');
                  }}
                  style={{
                    background: 'none',
                    border: 'none',
                    color: '#3b82f6',
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    fontSize: '0.9rem'
                  }}
                >
                  Sign Up
                </button>
              </>
            )}
          </div>
          

        </div>
        
        {/* Password Reset Modal */}
        {showPasswordReset && (
          <div style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1000
          }}>
            <div style={{
              background: 'white',
              borderRadius: '15px',
              padding: '30px',
              boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
              maxWidth: '400px',
              width: '90%'
            }}>
              {!showNewPasswordForm ? (
                <>
                  <h3 style={{ margin: '0 0 20px 0', color: '#374151', textAlign: 'center' }}>
                    🔐 Reset Password
                  </h3>
                  
                  {resetSuccess ? (
                    <>
                      <div style={{
                        background: '#d1fae5',
                        color: '#065f46',
                        padding: '15px',
                        borderRadius: '8px',
                        marginBottom: '20px',
                        textAlign: 'center'
                      }}>
                        ✅ Reset token sent! Check server console for the reset token.
                      </div>
                      
                      <input
                        type="text"
                        value={resetToken}
                        onChange={(e) => setResetToken(e.target.value)}
                        placeholder="Enter reset token from console"
                        style={{
                          width: '100%',
                          padding: '12px 16px',
                          border: '1px solid #d1d5db',
                          borderRadius: '8px',
                          fontSize: '0.9rem',
                          outline: 'none',
                          margin: '15px 0',
                          boxSizing: 'border-box'
                        }}
                      />
                      
                      <button
                        onClick={() => {
                          if (resetToken.trim()) {
                            setShowNewPasswordForm(true);
                            setAuthError('');
                          } else {
                            setAuthError('Please enter the reset token');
                          }
                        }}
                        style={{
                          width: '100%',
                          padding: '12px 16px',
                          background: '#10b981',
                          color: 'white',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: 'pointer'
                        }}
                      >
                        Continue
                      </button>
                    </>
                  ) : (
                    <>
                      <p style={{ margin: '0 0 20px 0', color: '#6b7280', textAlign: 'center' }}>
                        Enter your email to receive a reset token.
                      </p>
                      
                      {authError && (
                        <div style={{
                          background: '#fef2f2',
                          color: '#dc2626',
                          padding: '10px 15px',
                          borderRadius: '8px',
                          marginBottom: '15px',
                          fontSize: '0.9rem'
                        }}>
                          {authError}
                        </div>
                      )}
                      
                      <input
                        type="email"
                        value={resetEmail}
                        onChange={(e) => setResetEmail(e.target.value)}
                        placeholder="Enter your email"
                        style={{
                          width: '100%',
                          padding: '12px 16px',
                          border: '1px solid #d1d5db',
                          borderRadius: '8px',
                          fontSize: '0.9rem',
                          outline: 'none',
                          marginBottom: '20px',
                          boxSizing: 'border-box'
                        }}
                      />
                      
                      <div style={{ display: 'flex', gap: '10px' }}>
                        <button
                          onClick={() => {
                            setShowPasswordReset(false);
                            setResetEmail('');
                            setAuthError('');
                          }}
                          style={{
                            flex: 1,
                            padding: '10px 20px',
                            background: '#f3f4f6',
                            color: '#374151',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: 'pointer'
                          }}
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handlePasswordReset}
                          style={{
                            flex: 1,
                            padding: '10px 20px',
                            background: '#3b82f6',
                            color: 'white',
                            border: 'none',
                            borderRadius: '8px',
                            cursor: 'pointer'
                          }}
                        >
                          Send Reset
                        </button>
                      </div>
                    </>
                  )}
                </>
              ) : (
                <>
                  <h3 style={{ margin: '0 0 20px 0', color: '#374151', textAlign: 'center' }}>
                    🔑 Set New Password
                  </h3>
                  
                  {authError && (
                    <div style={{
                      background: '#fef2f2',
                      color: '#dc2626',
                      padding: '10px 15px',
                      borderRadius: '8px',
                      marginBottom: '15px',
                      fontSize: '0.9rem'
                    }}>
                      {authError}
                    </div>
                  )}
                  
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="New password (min 6 chars)"
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      fontSize: '0.9rem',
                      outline: 'none',
                      marginBottom: '15px',
                      boxSizing: 'border-box'
                    }}
                  />
                  
                  <input
                    type="password"
                    value={confirmNewPassword}
                    onChange={(e) => setConfirmNewPassword(e.target.value)}
                    placeholder="Confirm new password"
                    style={{
                      width: '100%',
                      padding: '12px 16px',
                      border: '1px solid #d1d5db',
                      borderRadius: '8px',
                      fontSize: '0.9rem',
                      outline: 'none',
                      marginBottom: '20px',
                      boxSizing: 'border-box'
                    }}
                  />
                  
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button
                      onClick={() => setShowNewPasswordForm(false)}
                      style={{
                        flex: 1,
                        padding: '10px 20px',
                        background: '#f3f4f6',
                        color: '#374151',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer'
                      }}
                    >
                      Back
                    </button>
                    <button
                      onClick={handleNewPassword}
                      style={{
                        flex: 1,
                        padding: '10px 20px',
                        background: '#dc2626',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        cursor: 'pointer'
                      }}
                    >
                      Reset Password
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  const currentMessages = selectedUser ? (conversations[selectedUser.name] || []) : [];

  // Admin Dashboard
  if (isAdmin) {
    return (
      <div style={{
        minHeight: '100vh',
        background: '#f8fafc',
        fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
      }}>
        {/* Admin Header */}
        <div style={{
          background: 'linear-gradient(135deg, #1f2937 0%, #374151 100%)',
          color: 'white',
          padding: '20px 40px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
        }}>
          <div>
            <h1 style={{ margin: '0', fontSize: '1.8rem', fontWeight: '700' }}>
              🛡️ ChatFlow Admin Dashboard
            </h1>
            <p style={{ margin: '5px 0 0 0', opacity: '0.8' }}>System Overview & Management</p>
          </div>
          <button
            onClick={() => {
              localStorage.removeItem('chatflow_user');
              setIsAdmin(false);
              setShowNameForm(true);
              setUserName('');
              setPassword('');
              setUserFullName('');
              setUserFirstName('');
            }}
            style={{
              padding: '10px 20px',
              background: 'rgba(255,255,255,0.2)',
              color: 'white',
              border: '1px solid rgba(255,255,255,0.3)',
              borderRadius: '8px',
              cursor: 'pointer',
              fontWeight: '500'
            }}
          >
            🚪 Logout
          </button>
        </div>

        {/* Stats Cards */}
        <div style={{
          padding: '40px',
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: '24px',
          maxWidth: '1200px',
          margin: '0 auto'
        }}>
          {/* Total Users Card */}
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '24px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
            border: '1px solid #e5e7eb'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
              <div style={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #3b82f6, #1d4ed8)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginRight: '16px'
              }}>
                <span style={{ fontSize: '1.5rem' }}>👥</span>
              </div>
              <div>
                <h3 style={{ margin: '0', color: '#1f2937', fontSize: '1.1rem' }}>Total Users</h3>
                <p style={{ margin: '4px 0 0 0', color: '#6b7280', fontSize: '0.9rem' }}>Registered accounts</p>
              </div>
            </div>
            <div style={{ fontSize: '2.5rem', fontWeight: '700', color: '#1f2937' }}>
              {adminStats.totalUsers || 0}
            </div>
            <div style={{ color: '#10b981', fontSize: '0.9rem', marginTop: '8px' }}>
              ↑ +{adminStats.newUsersToday || 0} today
            </div>
          </div>

          {/* Online Users Card */}
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '24px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
            border: '1px solid #e5e7eb'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
              <div style={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #10b981, #059669)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginRight: '16px'
              }}>
                <span style={{ fontSize: '1.5rem' }}>🟢</span>
              </div>
              <div>
                <h3 style={{ margin: '0', color: '#1f2937', fontSize: '1.1rem' }}>Online Now</h3>
                <p style={{ margin: '4px 0 0 0', color: '#6b7280', fontSize: '0.9rem' }}>Active users</p>
              </div>
            </div>
            <div style={{ fontSize: '2.5rem', fontWeight: '700', color: '#1f2937' }}>
              {adminStats.onlineUsers || 0}
            </div>
            <div style={{ color: '#6b7280', fontSize: '0.9rem', marginTop: '8px' }}>
              Currently active
            </div>
          </div>

          {/* Messages Card */}
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '24px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
            border: '1px solid #e5e7eb'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
              <div style={{
                width: '48px',
                height: '48px',
                borderRadius: '12px',
                background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginRight: '16px'
              }}>
                <span style={{ fontSize: '1.5rem' }}>💬</span>
              </div>
              <div>
                <h3 style={{ margin: '0', color: '#1f2937', fontSize: '1.1rem' }}>Total Messages</h3>
                <p style={{ margin: '4px 0 0 0', color: '#6b7280', fontSize: '0.9rem' }}>All time</p>
              </div>
            </div>
            <div style={{ fontSize: '2.5rem', fontWeight: '700', color: '#1f2937' }}>
              {adminStats.totalMessages || 0}
            </div>
            <div style={{ color: '#8b5cf6', fontSize: '0.9rem', marginTop: '8px' }}>
              ↑ +{adminStats.messagesToday || 0} today
            </div>
          </div>


        </div>

        {/* User Management Section */}
        <div style={{
          padding: '0 40px 40px',
          maxWidth: '1200px',
          margin: '0 auto'
        }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '24px',
            marginBottom: '24px'
          }}>
            {/* Online Users */}
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '24px',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
              border: '1px solid #e5e7eb'
            }}>
              <h3 style={{ margin: '0 0 20px 0', color: '#1f2937', fontSize: '1.2rem' }}>
                🟢 Online Users ({adminStats.onlineUsers || 0})
              </h3>
              <div style={{ maxHeight: '300px', overflowY: 'auto' }}>
                {adminStats.onlineUsersList && adminStats.onlineUsersList.length > 0 ? (
                  adminStats.onlineUsersList.map((user, idx) => (
                    <div key={idx} style={{
                      padding: '12px 0',
                      borderBottom: idx < adminStats.onlineUsersList.length - 1 ? '1px solid #f3f4f6' : 'none',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px'
                    }}>
                      <div style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '50%',
                        background: '#10b981',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        fontSize: '0.8rem',
                        fontWeight: 'bold'
                      }}>
                        {user.fullName.charAt(0).toUpperCase()}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ fontWeight: '500', color: '#1f2937', fontSize: '0.9rem' }}>
                          {user.fullName}
                        </div>
                        <div style={{ color: '#6b7280', fontSize: '0.8rem' }}>
                          {user.email}
                        </div>
                      </div>
                      <div style={{
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        background: '#10b981'
                      }}></div>
                    </div>
                  ))
                ) : (
                  <div style={{ color: '#6b7280', textAlign: 'center', padding: '20px' }}>
                    No users currently online
                  </div>
                )}
              </div>
            </div>

            {/* Registration Stats */}
            <div style={{
              background: 'white',
              borderRadius: '12px',
              padding: '24px',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
              border: '1px solid #e5e7eb'
            }}>
              <h3 style={{ margin: '0 0 20px 0', color: '#1f2937', fontSize: '1.2rem' }}>
                📊 Registration Analytics
              </h3>
              <div style={{ display: 'grid', gap: '16px' }}>
                <div style={{
                  padding: '16px',
                  background: '#f0f9ff',
                  borderRadius: '8px',
                  border: '1px solid #0ea5e9'
                }}>
                  <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#0c4a6e' }}>
                    {adminStats.newUsersToday || 0}
                  </div>
                  <div style={{ color: '#075985', fontSize: '0.9rem' }}>New users today</div>
                </div>
                <div style={{
                  padding: '16px',
                  background: '#f0fdf4',
                  borderRadius: '8px',
                  border: '1px solid #10b981'
                }}>
                  <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#065f46' }}>
                    {adminStats.newUsersThisWeek || 0}
                  </div>
                  <div style={{ color: '#047857', fontSize: '0.9rem' }}>New users this week</div>
                </div>
                <div style={{
                  padding: '16px',
                  background: '#fefce8',
                  borderRadius: '8px',
                  border: '1px solid #eab308'
                }}>
                  <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#a16207' }}>
                    {adminStats.verifiedUsers || 0}
                  </div>
                  <div style={{ color: '#92400e', fontSize: '0.9rem' }}>Verified users</div>
                </div>
              </div>
            </div>
          </div>

          {/* All Users Table */}
          <div style={{
            background: 'white',
            borderRadius: '12px',
            padding: '24px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)',
            border: '1px solid #e5e7eb'
          }}>
            <h3 style={{ margin: '0 0 20px 0', color: '#1f2937', fontSize: '1.3rem' }}>
              👥 All Registered Users ({adminStats.totalUsers || 0})
            </h3>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '2px solid #f3f4f6' }}>
                    <th style={{ padding: '12px', textAlign: 'left', color: '#374151', fontWeight: '600' }}>User</th>
                    <th style={{ padding: '12px', textAlign: 'left', color: '#374151', fontWeight: '600' }}>Email</th>

                    <th style={{ padding: '12px', textAlign: 'left', color: '#374151', fontWeight: '600' }}>Registered</th>
                    <th style={{ padding: '12px', textAlign: 'left', color: '#374151', fontWeight: '600' }}>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {adminStats.allUsers && adminStats.allUsers.length > 0 ? (
                    adminStats.allUsers.map((user, idx) => {
                      const isOnline = adminStats.onlineUsersList?.some(ou => ou.id === user.email);
                      return (
                        <tr key={idx} style={{
                          borderBottom: '1px solid #f3f4f6',
                          backgroundColor: idx % 2 === 0 ? '#f9fafb' : 'white'
                        }}>
                          <td style={{ padding: '12px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                              <div style={{
                                width: '32px',
                                height: '32px',
                                borderRadius: '50%',
                                background: '#3b82f6',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white',
                                fontSize: '0.8rem',
                                fontWeight: 'bold'
                              }}>
                                {user.fullName.charAt(0).toUpperCase()}
                              </div>
                              <div>
                                <div style={{ fontWeight: '500', color: '#1f2937' }}>{user.fullName}</div>
                              </div>
                            </div>
                          </td>
                          <td style={{ padding: '12px', color: '#6b7280' }}>{user.email}</td>
                          <td style={{ padding: '12px', color: '#6b7280' }}>
                            {new Date(user.createdAt).toLocaleDateString()}
                          </td>
                          <td style={{ padding: '12px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <div style={{
                                width: '8px',
                                height: '8px',
                                borderRadius: '50%',
                                background: isOnline ? '#10b981' : '#6b7280'
                              }}></div>
                              <span style={{
                                color: isOnline ? '#10b981' : '#6b7280',
                                fontSize: '0.8rem',
                                fontWeight: '500'
                              }}>
                                {isOnline ? 'Online' : 'Offline'}
                              </span>
                              {user.emailVerified && (
                                <span style={{
                                  background: '#d1fae5',
                                  color: '#065f46',
                                  padding: '2px 6px',
                                  borderRadius: '4px',
                                  fontSize: '0.7rem',
                                  fontWeight: '500'
                                }}>
                                  Verified
                                </span>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  ) : (
                    <tr>
                      <td colSpan="4" style={{ padding: '40px', textAlign: 'center', color: '#6b7280' }}>
                        No users registered yet
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{
      height: '100vh',
      display: 'flex',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
    }}>
      {/* Logout Confirmation Popup */}
      {showLogoutConfirm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            background: 'white',
            borderRadius: '15px',
            padding: '30px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
            textAlign: 'center',
            maxWidth: '400px',
            width: '90%'
          }}>
            <h3 style={{ margin: '0 0 15px 0', color: '#374151' }}>Confirm Logout</h3>
            <p style={{ margin: '0 0 25px 0', color: '#6b7280' }}>
              Are you sure you want to logout? You will lose your current session.
            </p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'center' }}>
              <button
                onClick={cancelLogout}
                style={{
                  padding: '10px 20px',
                  background: '#f3f4f6',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                No
              </button>
              <button
                onClick={confirmLogout}
                style={{
                  padding: '10px 20px',
                  background: '#dc2626',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  fontWeight: '500'
                }}
              >
                Yes, Logout
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Sidebar - User List */}
      <div style={{
        width: '300px',
        background: '#f0f2f5',
        borderRight: '1px solid #e4e6ea',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {/* Header */}
        <div style={{
          padding: '20px',
          background: '#00a884',
          color: 'white'
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <h2 style={{ margin: '0', fontSize: '1.2rem' }}>💬 ChatFlow</h2>
              <p style={{ margin: '5px 0 0 0', fontSize: '0.9rem', opacity: '0.8' }}>
                Welcome, {userFirstName || userName}! 
                <span style={{ color: connected ? '#10b981' : '#ef4444', marginLeft: '8px' }}>
                  {connected ? '🟢 Online' : '🔴 Offline'}
                </span>
              </p>
            </div>
            <button
              onClick={handleLogout}
              style={{
                padding: '8px 16px',
                background: 'rgba(255,255,255,0.2)',
                color: 'white',
                border: '1px solid rgba(255,255,255,0.3)',
                borderRadius: '20px',
                fontSize: '0.8rem',
                cursor: 'pointer',
                fontWeight: '500'
              }}
            >
              Logout
            </button>
          </div>
        </div>

        {/* Online Users */}
        <div style={{
          flex: '1',
          overflowY: 'auto'
        }}>
          <div style={{
            padding: '10px 0',
            borderBottom: '1px solid #e4e6ea',
            fontSize: '0.9rem',
            color: '#667781',
            paddingLeft: '20px'
          }}>
            Online Users ({onlineUsers.length})
          </div>
          {onlineUsers.map(user => (
            <div
              key={user.id}
              onClick={() => selectUser(user)}
              style={{
                padding: '15px 20px',
                cursor: 'pointer',
                borderBottom: '1px solid #e4e6ea',
                background: selectedUser?.id === user.id ? '#e7f3ff' : 'transparent'
              }}
            >
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <div style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '50%',
                  background: '#00a884',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontWeight: 'bold'
                }}>
                  {user.name.charAt(0).toUpperCase()}
                </div>
                <div>
                  <div style={{ fontWeight: '500', color: '#111b21' }}>{user.name}</div>
                  <div style={{ fontSize: '0.8rem', color: '#667781' }}>Online</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Chat Area */}
      <div style={{
        flex: '1',
        display: 'flex',
        flexDirection: 'column',
        background: '#efeae2'
      }}>
        {selectedUser ? (
          <>
            {/* Chat Header */}
            <div style={{
              padding: '15px 20px',
              background: '#f0f2f5',
              borderBottom: '1px solid #e4e6ea',
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <div style={{
                width: '40px',
                height: '40px',
                borderRadius: '50%',
                background: '#00a884',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontWeight: 'bold'
              }}>
                {selectedUser.name.charAt(0).toUpperCase()}
              </div>
              <div>
                <div style={{ fontWeight: '500', color: '#111b21' }}>{selectedUser.name}</div>
                <div style={{ fontSize: '0.8rem', color: '#667781' }}>Online</div>
              </div>
            </div>

            {/* Messages */}
            <div style={{
              flex: '1',
              overflowY: 'auto',
              padding: '20px'
            }}>
              {currentMessages.map((m, idx) => {
                const isMyMessage = m.from === clientId || m.fromName === userFullName || m.fromName === userName;
                return (
                  <div key={idx} style={{
                    marginBottom: '10px',
                    display: 'flex',
                    justifyContent: isMyMessage ? 'flex-end' : 'flex-start'
                  }}>
                    <div style={{
                      maxWidth: '70%',
                      padding: '8px 12px',
                      borderRadius: '8px',
                      background: isMyMessage ? '#d9fdd3' : 'white',
                      boxShadow: '0 1px 2px rgba(0,0,0,0.1)'
                    }}>
                      <div style={{ fontSize: '0.9rem', color: '#111b21' }}>
                        {m.text}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Input Area */}
            <div style={{
              padding: '10px 20px',
              background: '#f0f2f5',
              borderTop: '1px solid #e4e6ea'
            }}>
              <div style={{
                display: 'flex',
                gap: '10px',
                alignItems: 'center'
              }}>
                <input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyPress={handleKeyPress}
                  placeholder={`Message ${selectedUser.name}...`}
                  style={{
                    flex: '1',
                    padding: '10px 15px',
                    border: 'none',
                    borderRadius: '20px',
                    fontSize: '0.9rem',
                    outline: 'none'
                  }}
                />
                <button
                  onClick={sendMessage}
                  disabled={!input.trim() || !connected}
                  style={{
                    width: '40px',
                    height: '40px',
                    borderRadius: '50%',
                    border: 'none',
                    background: (input.trim() && connected) ? '#00a884' : '#d1d7db',
                    color: 'white',
                    cursor: (input.trim() && connected) ? 'pointer' : 'not-allowed',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                >
                  ➤
                </button>
              </div>
            </div>
          </>
        ) : (
          <div style={{
            flex: '1',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#667781',
            fontSize: '1.1rem'
          }}>
            Select a user to start chatting
          </div>
        )}
      </div>
    </div>
  );
}